/**
 * @file notifications.api.ts
 * @description SysNot-1: client for the deterministic system-notifications
 * channel. Mirrors `tasks.api.ts:subscribeToTaskUpdates` — same JWT-via-
 * query-token approach because EventSource cannot send custom headers.
 */

// NOTE: this union is a partial mirror of the backend SSOT
// (`libs/common/src/enums/system-notification-type.enum.ts`) — it does not
// cover every type the producer can emit (tracked separately, S4-98
// SYSNOT-SSOT-RESIDUAL). `tool_summary` added here for S4-45 HIGH-2 so this
// specific type type-checks; the broader drift is out of scope for that fix.
export type SystemNotificationType = 'reminder' | 'memory' | 'routing' | 'tool_summary';

export interface SystemNotificationEvent {
  source: string;
  userId: string;
  type: SystemNotificationType;
  action: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

interface HeartbeatMessage {
  type: 'heartbeat';
}

type StreamMessage = SystemNotificationEvent | HeartbeatMessage;

/**
 * Open an EventSource to `/api/v1/notifications/stream`. JWT goes via the
 * `?token=` query-parameter because browsers do not let us set an
 * `Authorization` header on EventSource. The server's JwtAuthGuard already
 * accepts query-token (apps/user-panel/src/common/guards/jwt-auth.guard.ts).
 *
 * @param onEvent — fires only on real events (heartbeats are filtered).
 * @param onError — optional error handler. Note: EventSource auto-reconnects.
 * @returns disposer — call to close the connection.
 */
export function subscribeToSystemNotifications(
  onEvent: (event: SystemNotificationEvent) => void,
  onError?: (error: Event) => void,
): () => void {
  const token = localStorage.getItem('accessToken');
  const url = `/api/v1/notifications/stream${token ? `?token=${token}` : ''}`;

  const source = new EventSource(url);

  source.onmessage = (msg) => {
    try {
      const data = JSON.parse(msg.data) as StreamMessage;
      // Heartbeats keep the pipe warm — no UI action needed.
      if ('type' in data && data.type === 'heartbeat') return;
      onEvent(data as SystemNotificationEvent);
    } catch (error) {
      console.error('Failed to parse system.notification SSE payload:', error);
    }
  };

  source.onerror = (err) => {
    // EventSource auto-reconnects; we only surface for diagnostics.
    console.error('System notifications SSE error:', err);
    onError?.(err);
  };

  return () => source.close();
}
